import { Plus, Trash2 } from "lucide-react";
import * as React from "react";

import type { FormFieldSchema, FormSchema } from "@/api/forms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * LogicRuleEditor — visual builder cho show_if / require_if rules.
 *
 * Hỗ trợ: rule simple form `{op: [{var: <field>}, <value>]}` với op ∈ {==, !=, >, <, >=, <=}.
 * Rule phức tạp hơn (and/or) dùng raw JSON editor (advanced mode).
 */
type Op = "==" | "!=" | ">" | "<" | ">=" | "<=";
type SimpleRule = { op: Op; field: string; value: string };

const OPS: Op[] = ["==", "!=", ">", "<", ">=", "<="];

interface Props {
  field: FormFieldSchema;
  schema: FormSchema;
  onUpdate: (patch: Partial<FormFieldSchema>) => void;
}

export function LogicRuleEditor({ field, schema, onUpdate }: Props): JSX.Element {
  const otherFieldNames = collectFieldNames(schema, field.name);
  const [advanced, setAdvanced] = React.useState(false);
  const [rawShowIf, setRawShowIf] = React.useState(
    field.show_if ? JSON.stringify(field.show_if, null, 2) : "",
  );
  const [rawRequireIf, setRawRequireIf] = React.useState(
    field.require_if ? JSON.stringify(field.require_if, null, 2) : "",
  );

  React.useEffect(() => {
    setRawShowIf(field.show_if ? JSON.stringify(field.show_if, null, 2) : "");
    setRawRequireIf(field.require_if ? JSON.stringify(field.require_if, null, 2) : "");
  }, [field.name, field.show_if, field.require_if]);

  const applyRaw = (key: "show_if" | "require_if", raw: string) => {
    if (!raw.trim()) {
      onUpdate({ [key]: null });
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      onUpdate({ [key]: parsed });
    } catch {
      // im lặng — user tiếp tục nhập
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase font-semibold text-muted-foreground">
          Logic & điều kiện
        </div>
        <button
          className="text-[10px] text-primary hover:underline"
          onClick={() => setAdvanced((v) => !v)}
        >
          {advanced ? "Simple mode" : "Advanced (JSON)"}
        </button>
      </div>

      {advanced ? (
        <>
          <RuleField label="show_if (JSON-logic)">
            <textarea
              value={rawShowIf}
              onChange={(e) => {
                setRawShowIf(e.target.value);
                applyRaw("show_if", e.target.value);
              }}
              rows={3}
              className="w-full text-[11px] font-mono rounded border bg-background p-2"
              placeholder='{">":[{"var":"amount"},50000000]}'
            />
          </RuleField>
          <RuleField label="require_if (JSON-logic)">
            <textarea
              value={rawRequireIf}
              onChange={(e) => {
                setRawRequireIf(e.target.value);
                applyRaw("require_if", e.target.value);
              }}
              rows={3}
              className="w-full text-[11px] font-mono rounded border bg-background p-2"
              placeholder='{"==":[{"var":"is_urgent"},true]}'
            />
          </RuleField>
        </>
      ) : (
        <>
          <SimpleRuleEditor
            label="Hiện field này khi..."
            rule={field.show_if ?? null}
            otherFields={otherFieldNames}
            onChange={(v) => onUpdate({ show_if: v })}
          />
          <SimpleRuleEditor
            label="Bắt buộc khi..."
            rule={field.require_if ?? null}
            otherFields={otherFieldNames}
            onChange={(v) => onUpdate({ require_if: v })}
          />
        </>
      )}
    </div>
  );
}

function RuleField({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <label className="block text-xs font-medium mb-1">{label}</label>
      {children}
    </div>
  );
}

interface SimpleEditorProps {
  label: string;
  rule: Record<string, unknown> | null;
  otherFields: string[];
  onChange: (v: Record<string, unknown> | null) => void;
}

function SimpleRuleEditor({ label, rule, otherFields, onChange }: SimpleEditorProps): JSX.Element {
  const parsed = parseSimpleRule(rule);

  const update = (next: SimpleRule | null) => {
    if (next == null) {
      onChange(null);
      return;
    }
    const v = next.value;
    let parsedVal: unknown = v;
    if (v === "true") parsedVal = true;
    else if (v === "false") parsedVal = false;
    else if (/^-?\d+(\.\d+)?$/.test(v)) parsedVal = Number(v);
    onChange({ [next.op]: [{ var: next.field }, parsedVal] });
  };

  if (!parsed) {
    return (
      <RuleField label={label}>
        <Button
          size="sm"
          variant="outline"
          className="w-full"
          onClick={() =>
            update({
              op: "==",
              field: otherFields[0] ?? "",
              value: "",
            })
          }
        >
          <Plus className="size-3.5 mr-1" /> Thêm rule
        </Button>
      </RuleField>
    );
  }

  return (
    <RuleField label={label}>
      <div className="flex items-center gap-1">
        <select
          value={parsed.field}
          onChange={(e) => update({ ...parsed, field: e.target.value })}
          className="h-8 rounded border bg-background px-1 text-xs flex-1 min-w-0"
        >
          {otherFields.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
        <select
          value={parsed.op}
          onChange={(e) => update({ ...parsed, op: e.target.value as Op })}
          className="h-8 rounded border bg-background px-1 text-xs"
        >
          {OPS.map((op) => (
            <option key={op} value={op}>
              {op}
            </option>
          ))}
        </select>
        <Input
          value={parsed.value}
          onChange={(e) => update({ ...parsed, value: e.target.value })}
          placeholder="value"
          className="h-8 text-xs flex-1 min-w-0"
        />
        <Button
          size="icon"
          variant="ghost"
          className="size-7 shrink-0"
          onClick={() => update(null)}
        >
          <Trash2 className="size-3.5" />
        </Button>
      </div>
      <p className="text-[10px] text-muted-foreground mt-1">
        Cần phức tạp hơn (and/or)? → Advanced mode.
      </p>
    </RuleField>
  );
}

function parseSimpleRule(rule: Record<string, unknown> | null): SimpleRule | null {
  if (!rule) return null;
  const keys = Object.keys(rule);
  if (keys.length !== 1) return null;
  const op = keys[0] as Op;
  if (!OPS.includes(op)) return null;
  const args = (rule as Record<string, unknown[]>)[op];
  if (!Array.isArray(args) || args.length !== 2) return null;
  const left = args[0] as { var?: string };
  if (!left || typeof left !== "object" || typeof left.var !== "string") return null;
  return {
    op,
    field: left.var,
    value: String(args[1] ?? ""),
  };
}

function collectFieldNames(schema: FormSchema, exclude: string): string[] {
  const out: string[] = [];
  const walk = (xs: FormFieldSchema[]) => {
    for (const f of xs) {
      if (f.name !== exclude) out.push(f.name);
      if (f.fields) walk(f.fields);
    }
  };
  walk(schema.fields);
  return out;
}
